import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ClipboardList, Package } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { BotaoDoRaro, EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { Seletor } from '@/componentes/Seletor';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarAteDuasCasas, formatarData, formatarDataHora, formatarDecimal, formatarInteiro, formatarReais, interpretarNumero } from '@/lib/formato';
import { combinaComTermo } from '@/lib/texto';
import { numeroDaOrdem } from '@/modulos/oficina/ordem';
import { useSessao } from '@/modulos/sessao/consultas';

import { api, type MovimentoDaApi, type SaldoDaApi, type TipoDeMovimento } from './api';
import {
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  ehSituacaoDoEstoque as ehSituacao,
  montarBuscaDoEstoque,
  type FiltroDeSituacao,
  type SituacaoDoFiltro,
} from './busca';
import { faltaDoDeposito, opcaoVaziaDoDeposito } from './deposito';
import {
  explicarFalha,
  useApoioDoEstoque,
  useContar,
  useCriarDeposito,
  useCriarConversao,
  useCriarItem,
  useMovimentar,
  useNecessidade,
  useSaldos,
} from './consultas';
import { contarEstoque, ROTULO_DA_CATEGORIA, situacaoDoSaldo } from './saldo';
import { ajudaDaConversao, erroDaEmbalagem, erroDoFator, textoDaConversao } from './conversao';
import { CATEGORIAS_COM_APLICACAO } from './aplicacao';
import { AbaDasAplicacoes, AbaDasUnidades } from './UnidadesEAplicacoes';
import { custoSugerido, depositoSugerido, fraseDaContagem, opcaoDoItem, totalDaLinha, ultimaEntrada, unidadeDaCategoria } from './preenchimento';
import { useUltimoUsado } from './useUltimoUsado';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Estoque e almoxarifado — módulo 07 do mapa funcional v6; passo 7 do ciclo 1.
 *
 * ## Três números, e a tela escreve os três
 *
 * Físico, reservado e disponível. O v6 escreve o risco de misturar os três:
 * "prometer o mesmo estoque para duas manutenções". O disponível é o que se
 * pode prometer; o reservado já tem O.S.
 *
 * ## Tanque e almoxarifado na mesma lista
 *
 * A decisão 13 do dono pede saldo por depósito de diesel, ARLA e lubrificante.
 * O combustível vem do livro do tanque (módulo 08), sem cópia; a linha diz
 * "Tanque" para quem precisar saber de onde veio o número.
 */

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;
type Formulario = 'movimento' | 'contagem' | 'item' | 'deposito' | null;
/** Erro de cada campo, pelo nome dele. `null` = conferido e bom. */
type Erros = Readonly<Record<string, string | null>>;

/* O vocabulario do filtro e a paginacao moram em `busca.ts`, junto do
   validador da URL: duas listas de situacao divergiriam, e a do validador
   passaria a recusar o que a barra oferece. */
const ROTULO_DA_SITUACAO_DO_FILTRO: Readonly<Record<SituacaoDoFiltro, string>> = {
  abaixo: 'Abaixo do mínimo',
  sem_saldo: 'Sem saldo',
};

function chaveDoLocal(s: SaldoDaApi): string {
  return `${s.localTipo}:${s.localId}`;
}

/** Liga o campo à mensagem abaixo dele (p.16), para o leitor de tela. */
function marcaDeErro(erro: string | null | undefined, id: string) {
  return erro ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-erro` } : {};
}

function temErro(erros: Erros): boolean {
  return Object.values(erros).some(Boolean);
}

/** Só a recusa do SERVIDOR mora aqui; o erro de campo vai abaixo do campo. */
function Aviso({ recusa }: { readonly recusa: Recusa }) {
  if (recusa === null) return null;
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{recusa.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
    </div>
  );
}

/**
 * Número com unidade, em vermelho quando o próprio número é o alerta (R02,
 * p.31). A unidade continua cinza e sem negrito (p.12). Sem alerta, é a
 * `Medida` de sempre.
 */
function MedidaDeAlerta({
  valor,
  unidade,
  alerta,
}: {
  readonly valor: string;
  readonly unidade: string;
  readonly alerta: boolean;
}) {
  if (!alerta) return <Medida valor={valor} unidade={unidade} />;
  return (
    <span className="numero text-data-vencida">
      {valor}
      <span className="text-tinta-secundaria">{` ${unidade}`}</span>
    </span>
  );
}

const rota = getRouteApi('/estoque');

export function PaginaEstoque() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = (codigo: string) => permissoes.includes(codigo);

  const saldos = useSaldos();
  const base = useId();
  const [aberto, setAberto] = useState<Formulario>(null);
  /* A linha aberta no painel do livro de movimentos (o M22 do app, na mesa). */
  const [selecionado, setSelecionado] = useState<string | null>(null);
  /* OS QUATRO FILTROS MORAM NA URL (p.16): "olha o que esta abaixo do minimo
     no deposito da obra" e uma conversa por link, e o filtro que nasce do
     clique no indicador — que e o caminho mais usado — nao sobrevivia nem a um
     recarregamento. */
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const termo = busca.termo ?? '';
  const categoria = busca.categoria ?? '';
  const local = busca.local ?? '';
  const situacao: FiltroDeSituacao = busca.situacao ?? '';
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  const todos = useMemo(() => saldos.data ?? [], [saldos.data]);
  const contagem = useMemo(() => contarEstoque(todos), [todos]);
  const semSaldo = useMemo(() => todos.filter((s) => s.fisico <= 0).length, [todos]);
  const criticas = useMemo(() => todos.filter((s) => s.critico).length, [todos]);
  const categorias = useMemo(
    () => [...new Set(todos.map((s) => s.categoria))].sort((x, y) => x.localeCompare(y)),
    [todos],
  );
  const locais = useMemo(() => {
    const vistos = new Map<string, string>();
    for (const s of todos) vistos.set(chaveDoLocal(s), s.local);
    return [...vistos.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [todos]);
  const linhas = useMemo(
    () =>
      todos.filter(
        (s) =>
          combinaComTermo([s.produto, s.produtoCodigo], termo) &&
          (categoria === '' || s.categoria === categoria) &&
          (local === '' || chaveDoLocal(s) === local) &&
          (situacao === '' || (situacao === 'abaixo' ? s.abaixoDoMinimo : s.fisico <= 0)),
      ),
    [todos, termo, categoria, local, situacao],
  );

  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);

  /* Trocar filtro volta para a primeira página: ficar na página 3 depois de
     filtrar mostra tela vazia e parece defeito. */
  function filtrar(novo: {
    readonly termo?: string;
    readonly categoria?: string;
    readonly local?: string;
    readonly situacao?: FiltroDeSituacao;
  }) {
    /* Trocar o filtro e NAVEGAR, nao mexer em estado: assim o botao de voltar
       do navegador desfaz a troca, que e o que a pessoa espera dele. */
    void navegar({
      search: montarBuscaDoEstoque({
        termo: novo.termo ?? termo,
        categoria: novo.categoria ?? categoria,
        local: novo.local ?? local,
        situacao: novo.situacao ?? situacao,
        pagina: 1,
        porPagina,
      }),
    });
  }

  function limparFiltros() {
    filtrar({ termo: '', categoria: '', local: '', situacao: '' });
  }

  const chips: readonly ChipFiltro[] = [
    ...(termo ? [{ id: 'termo', rotulo: `Busca: ${termo}`, aoRemover: () => { filtrar({ termo: '' }); } }] : []),
    ...(categoria
      ? [
          {
            id: 'categoria',
            rotulo: `Categoria: ${ROTULO_DA_CATEGORIA[categoria] ?? categoria}`,
            aoRemover: () => { filtrar({ categoria: '' }); },
          },
        ]
      : []),
    ...(local
      ? [
          {
            id: 'local',
            rotulo: `Local: ${locais.find(([chave]) => chave === local)?.[1] ?? local}`,
            aoRemover: () => { filtrar({ local: '' }); },
          },
        ]
      : []),
    ...(situacao
      ? [
          {
            id: 'situacao',
            rotulo: `Situação: ${ROTULO_DA_SITUACAO_DO_FILTRO[situacao]}`,
            aoRemover: () => { filtrar({ situacao: '' }); },
          },
        ]
      : []),
  ];

  function fechar() {
    setAberto(null);
  }

  const COLUNAS: readonly Coluna<SaldoDaApi>[] = [
    {
      chave: 'item',
      rotulo: 'Item',
      /* O nome vai a até duas linhas, quebrando entre palavras (p.18); o
         código e a categoria, embaixo, não quebram. */
      celula: (s) => (
        <span className="flex flex-col">
          <span className="txt-corpo line-clamp-2 min-w-40 whitespace-normal text-pretty text-tinta" title={s.produto}>
            {s.produto}
          </span>
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
            {/* Código em monoespaçada, para comparar na vertical (p.18). */}
            <span className="codigo">{s.produtoCodigo}</span>
            {` · ${ROTULO_DA_CATEGORIA[s.categoria] ?? s.categoria}${s.critico ? ' · crítico' : ''}`}
          </span>
        </span>
      ),
    },
    {
      chave: 'local',
      rotulo: 'Local',
      celula: (s) => (
        <span className="flex flex-col">
          <span className="txt-corpo line-clamp-2 min-w-32 whitespace-normal text-pretty text-tinta" title={s.local}>
            {s.local}
          </span>
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
            {s.localTipo === 'tanque' ? 'Tanque' : s.endereco ? `Endereço ${s.endereco}` : 'Sem endereço'}
          </span>
        </span>
      ),
    },
    {
      chave: 'fisico',
      rotulo: 'Físico',
      historia: 'COL-EST-01',
      alinhamento: 'numero',
      celula: (s) => <Medida valor={formatarDecimal(s.fisico, 1)} unidade={s.unidade} />,
    },
    {
      chave: 'reservado',
      rotulo: 'Reservado',
      historia: 'COL-EST-02',
      alinhamento: 'numero',
      /* Secundária (R01, p.28): sai abaixo de 1280 px. O que se pode prometer
         é o Disponível, que fica; a Situação diz "Todo reservado". */
      prioridade: 'secundaria',
      celula: (s) => <Medida valor={formatarDecimal(s.reservado, 1)} unidade={s.unidade} />,
    },
    {
      chave: 'disponivel',
      rotulo: 'Disponível',
      historia: 'NUM-EST-04',
      alinhamento: 'numero',
      /* R02: abaixo do mínimo, o disponível é o próprio alerta — em vermelho,
         com o rótulo escrito na coluna Situação. */
      celula: (s) => (
        <MedidaDeAlerta valor={formatarDecimal(s.disponivel, 1)} unidade={s.unidade} alerta={s.abaixoDoMinimo} />
      ),
    },
    {
      chave: 'minimo',
      rotulo: 'Mínimo',
      alinhamento: 'numero',
      /* Secundária (R01, p.28): abaixo do mínimo, o Disponível sai em
         vermelho e a Situação diz por escrito. */
      prioridade: 'secundaria',
      celula: (s) =>
        s.localTipo === 'tanque' ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <Medida valor={formatarDecimal(s.estoqueMinimo, 1)} unidade={s.unidade} />
        ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (s) => (
        <span className={`txt-corpo text-tinta ${s.abaixoDoMinimo ? 'font-semibold' : ''}`}>{situacaoDoSaldo(s)}</span>
      ),
    },
  ];

  const saldoAberto = todos.find((s) => chaveDoSaldo(s) === selecionado) ?? null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Almoxarifado"
          titulo="Estoque"
          subtitulo="O que há, o que já está prometido a uma O.S., e o que falta repor"
          acoes={
            /* Cada ação curta abre em MODAL ao centro (R01, p.29). Secundários
               à esquerda, o laranja à direita (p.15). */
            <>
              {pode('estoque.ajustar') ? (
                <HistoriaNaMesa codigo="BTN-EST-03" className="flex">
                  <JanelaDeAcao
                    rotulo="Registrar contagem"
                    titulo="Registrar contagem"
                    aberta={aberto === 'contagem'}
                    aoAbrir={() => {
                      setAberto('contagem');
                    }}
                    aoFechar={fechar}
                  >
                    <FormularioDeContagem aoFechar={fechar} />
                  </JanelaDeAcao>
                </HistoriaNaMesa>
              ) : null}
              {pode('estoque.gerir') ? (
                <JanelaDeAcao
                  rotulo="Cadastrar depósito"
                  titulo="Cadastrar depósito"
                  aberta={aberto === 'deposito'}
                  aoAbrir={() => {
                    setAberto('deposito');
                  }}
                  aoFechar={fechar}
                >
                  <FormularioDeDeposito aoFechar={fechar} />
                </JanelaDeAcao>
              ) : null}
              {pode('estoque.gerir') ? (
                <JanelaDeAcao
                  rotulo="Cadastrar item"
                  titulo="Cadastrar item"
                  aberta={aberto === 'item'}
                  aoAbrir={() => {
                    setAberto('item');
                  }}
                  aoFechar={fechar}
                >
                  <FormularioDeItem aoFechar={fechar} />
                </JanelaDeAcao>
              ) : null}
              {pode('estoque.movimentar') ? (
                <JanelaDeAcao
                  rotulo="Lançar movimento"
                  titulo="Lançar movimento"
                  nivel="primario"
                  aberta={aberto === 'movimento'}
                  aoAbrir={() => {
                    setAberto('movimento');
                  }}
                  aoFechar={fechar}
                >
                  <FormularioDeMovimento aoFechar={fechar} />
                </JanelaDeAcao>
              ) : null}
            </>
          }
        />

        <FaixaIndicadores>
          {/* Contexto é a BASE, em frase curta (p.17): "2 de 12 posições". A
              definição — o que é posição, o que é reserva — vai uma vez, na
              linha de ajuda abaixo da faixa: no card ela quebrava em três
              linhas a 1280 e a 1024. */}
          <CardIndicador
            rotulo="Abaixo do mínimo"
            icone={Package}
            carregando={saldos.isPending}
            /* R02 (p.31): o ícone e o número de alerta no MESMO tom. Em alerta
               o número sai vermelho, então o bloco é vermelho também — âmbar e
               vermelho no mesmo card eram dois sinais para um fato. */
            tom="parada"
            /* R02: o próprio número é o alerta. Zero sai em preto. */
            alerta={contagem.abaixoDoMinimo > 0}
            valor={formatarInteiro(contagem.abaixoDoMinimo)}
            contexto={saldos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'posição' : 'posições'}`}
            {...(todos.length > 0 ? { proporcao: contagem.abaixoDoMinimo / todos.length } : {})}
            aoAbrir={() => {
              filtrar({ situacao: 'abaixo' });
            }}
          />
          <CardIndicador
            rotulo="Críticos abaixo"
            icone={Package}
            carregando={saldos.isPending}
            tom="parada"
            alerta={contagem.criticosAbaixo > 0}
            valor={formatarInteiro(contagem.criticosAbaixo)}
            contexto={saldos.isPending ? '' : `de ${formatarInteiro(criticas)} ${criticas === 1 ? 'posição crítica' : 'posições críticas'}`}
          />
          <CardIndicador
            rotulo="Com reserva"
            icone={ClipboardList}
            carregando={saldos.isPending}
            tom="manutencao"
            valor={formatarInteiro(contagem.comReserva)}
            contexto={saldos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'posição' : 'posições'}`}
          />
          {/* "Sem saldo" no lugar de "Locais": quantos locais existem é
              inventário, e não pergunta da manhã; o que acabou é (p.17). */}
          <CardIndicador
            rotulo="Sem saldo"
            icone={Package}
            carregando={saldos.isPending}
            valor={formatarInteiro(semSaldo)}
            contexto={saldos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'posição' : 'posições'}`}
            {...(todos.length > 0 ? { proporcao: semSaldo / todos.length } : {})}
            aoAbrir={() => {
              filtrar({ situacao: 'sem_saldo' });
            }}
          />
        </FaixaIndicadores>
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          Posição é um item num local: o mesmo filtro pode faltar num depósito e sobrar no outro. Reserva é peça
          prometida a uma O.S. aberta.
        </p>

        <NecessidadeDasPreventivas />

        <BarraFiltros
          chips={chips}
          /* Sem resposta não há contagem: "0 de 0" seria um número (p.32). */
          contagem={
            saldos.isPending
              ? null
              : `${formatarInteiro(linhas.length)} de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'posição' : 'posições'}`
          }
          aoLimpar={limparFiltros}
        >
          <div className="min-w-0 flex-1">
            <CampoTexto
              rotulo="Buscar"
              valor={termo}
              placeholder="Nome ou código do item"
              aplicado={termo !== ''}
              aoMudar={(novo) => {
                filtrar({ termo: novo });
              }}
            />
          </div>
          {/* As categorias e os locais saem do próprio saldo: enquanto ele não
              chega, o controle é esqueleto (p.32) — uma lista só com "Todas"
              mostraria o filtro da URL como se não houvesse filtro. */}
          {saldos.isPending ? (
            <div className="w-40">
              <Campo id={`${base}-categoria`} rotulo="Categoria">
                <ControleCarregando oQue="Carregando as categorias" />
              </Campo>
            </div>
          ) : (
            <CampoSelecao
              rotulo="Categoria"
              valor={categoria}
              rotuloVazio="Todas"
              aplicado={categoria !== ''}
              opcoes={categorias.map((c) => ({ valor: c, rotulo: ROTULO_DA_CATEGORIA[c] ?? c }))}
              aoMudar={(novo) => {
                filtrar({ categoria: novo });
              }}
            />
          )}
          {saldos.isPending ? (
            <div className="w-40">
              <Campo id={`${base}-local`} rotulo="Local">
                <ControleCarregando oQue="Carregando os locais" />
              </Campo>
            </div>
          ) : (
            <CampoSelecao
              rotulo="Local"
              valor={local}
              rotuloVazio="Todos"
              aplicado={local !== ''}
              opcoes={locais.map(([chave, nome]) => ({ valor: chave, rotulo: nome }))}
              aoMudar={(novo) => {
                filtrar({ local: novo });
              }}
            />
          )}
          <CampoSelecao
            rotulo="Situação"
            valor={situacao}
            rotuloVazio="Todas"
            aplicado={situacao !== ''}
            opcoes={(Object.keys(ROTULO_DA_SITUACAO_DO_FILTRO) as Exclude<FiltroDeSituacao, ''>[]).map((s) => ({
              valor: s,
              rotulo: ROTULO_DA_SITUACAO_DO_FILTRO[s],
            }))}
            aoMudar={(novo) => {
              filtrar({ situacao: ehSituacao(novo) ? novo : '' });
            }}
          />
        </BarraFiltros>

        <div data-painel-origem>
        <Tabela
          rotulo="Saldo por item e local"
          colunas={COLUNAS}
          linhas={visiveis}
          alturaDupla
          chaveDaLinha={chaveDoSaldo}
          selecionada={selecionado}
          aoSelecionar={(s) => {
            setSelecionado(chaveDoSaldo(s));
          }}
          carregando={saldos.isPending}
          erro={
            saldos.isError ? (
              <EstadoVazio
                titulo="Não foi possível carregar o estoque"
                descricao={explicarFalha(saldos.error).comoResolver}
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void saldos.refetch();
                    }}
                  >
                    Carregar de novo
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            todos.length > 0 ? (
              <EstadoVazio
                titulo="Nenhuma posição com esses filtros"
                descricao="Troque a busca, a categoria, o local ou a situação, ou limpe os filtros para ver o estoque inteiro."
                acao={
                  <Botao nivel="secundario" onClick={limparFiltros}>
                    Limpar filtros
                  </Botao>
                }
              />
            ) : (
              <EstadoVazio
                titulo="Nenhum item com saldo ainda"
                descricao="Cadastre os depósitos e os itens, e lance a primeira entrada com a nota. Para o saldo que já existe no almoxarifado, registre uma contagem."
              />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={inicio + 1}
              ultimo={inicio + visiveis.length}
              total={linhas.length}
              pagina={paginaSegura}
              paginas={paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={(nova) => {
                void navegar({
                  search: montarBuscaDoEstoque({ termo, categoria, local, situacao, pagina: nova, porPagina }),
                });
              }}
              aoMudarPorPagina={(novo) => {
                void navegar({
                  search: montarBuscaDoEstoque({ termo, categoria, local, situacao, pagina: 1, porPagina: novo }),
                });
              }}
            />
          }
        />
        </div>
      </div>
    </div>
      {saldoAberto ? (
        <PainelDoSaldo
          key={chaveDoSaldo(saldoAberto)}
          saldo={saldoAberto}
          podeGerir={pode('estoque.gerir')}
          podeValidar={pode('aplicacao.validar')}
          aoFechar={() => {
            setSelecionado(null);
          }}
        />
      ) : null}
    </div>
  );
}

function chaveDoSaldo(s: SaldoDaApi): string {
  return `${s.produtoId}-${s.localTipo}-${s.localId}`;
}

/* ========================================================================
 * O LIVRO DE MOVIMENTOS DO ITEM (M22 do app, na mesa)
 * ===================================================================== */

/** Uma linha do livro: o que foi, de onde veio, quem lançou, e a quantidade com sinal. */
interface LinhaDoLivro {
  readonly chave: string;
  readonly titulo: string;
  readonly detalhe: string;
  readonly quantidade: number;
  readonly quando: string;
}

/**
 * O movimento em frase, como o app escreve (`src/mobile/estoque.ts`). A
 * transferência sai de um depósito e entra em outro: o sinal é o do depósito
 * aberto no painel.
 */
function linhaDoLivro(m: MovimentoDaApi, deposito: string): LinhaDoLivro {
  const juntar = (...partes: (string | null)[]) => partes.filter(Boolean).join(' · ');
  const os = m.ordemNumero !== null ? numeroDaOrdem(m.ordemNumero) : null;
  switch (m.tipo) {
    case 'entrada':
      return { chave: m.id, titulo: juntar('Entrada', m.documento), detalhe: juntar(informada(m), m.fornecedor, m.registradoPor), quantidade: m.quantidade, quando: m.ocorridoEm };
    case 'saida':
      return { chave: m.id, titulo: juntar('Saída', os), detalhe: juntar(informada(m), m.equipamento, m.registradoPor), quantidade: -m.quantidade, quando: m.ocorridoEm };
    case 'devolucao':
      return { chave: m.id, titulo: juntar('Devolução', os), detalhe: juntar(m.equipamento, m.registradoPor), quantidade: m.quantidade, quando: m.ocorridoEm };
    case 'transferencia': {
      const chegando = m.depositoDestino === deposito;
      return {
        chave: m.id,
        titulo: chegando ? `Transferência · de ${m.deposito}` : `Transferência · para ${m.depositoDestino ?? 'outro depósito'}`,
        detalhe: juntar(m.registradoPor),
        quantidade: chegando ? m.quantidade : -m.quantidade,
        quando: m.ocorridoEm,
      };
    }
    default:
      return {
        chave: m.id,
        titulo: 'Ajuste de saldo',
        detalhe: juntar(m.motivo, m.registradoPor),
        quantidade: m.tipo === 'ajuste_entrada' ? m.quantidade : -m.quantidade,
        quando: m.ocorridoEm,
      };
  }
}

/** 0088 · "2 CX 12": o que foi digitado na embalagem, quando houve. */
function informada(m: MovimentoDaApi): string | null {
  return m.quantidadeInformada && m.unidadeInformada ? `${formatarAteDuasCasas(m.quantidadeInformada)} ${m.unidadeInformada}` : null;
}

/** "+6", "−2": o sinal de menos tipográfico, como o app escreve. */
function comSinal(q: number): string {
  return `${q > 0 ? '+' : q < 0 ? '−' : ''}${formatarAteDuasCasas(Math.abs(q))}`;
}

const LIMITE_DO_LIVRO = 50;

/**
 * O painel do item no local: os três números e o livro de movimentos — de onde
 * veio cada entrada e para onde foi cada saída. O livro é só de inserção:
 * corrigir é lançar o ajuste, não editar a linha.
 */
function PainelDoSaldo({
  saldo,
  podeGerir,
  podeValidar,
  aoFechar,
}: {
  readonly saldo: SaldoDaApi;
  readonly podeGerir: boolean;
  readonly podeValidar: boolean;
  readonly aoFechar: () => void;
}) {
  const doDeposito = saldo.localTipo === 'deposito';
  const movimentos = useQuery({
    queryKey: ['estoque', 'movimentos', saldo.produtoId, saldo.localId],
    enabled: doDeposito,
    queryFn: async () => (await api.movimentos({ produtoId: saldo.produtoId, depositoId: saldo.localId })).movimentos,
  });
  const linhas = (movimentos.data ?? []).slice(0, LIMITE_DO_LIVRO).map((m) => linhaDoLivro(m, saldo.local));

  const listaDeMovimentos = !doDeposito ? (
    <EstadoVazio
      titulo="Os lançamentos do tanque ficam em Combustível"
      descricao="O saldo do tanque vem dos lançamentos do combustível (módulo 08). Abra Combustível › Tanques para ver cada abastecimento e cada entrada."
    />
  ) : movimentos.isPending ? (
    <Carregando oQue="Carregando os movimentos" className="flex flex-col gap-3">
      {['w-3/4', 'w-2/3', 'w-4/5'].map((largura) => (
        <span key={largura} className="flex items-center justify-between gap-4 border-b border-borda-sutil py-2">
          <span className="flex w-full flex-col gap-2">
            <Barra className={`h-3 ${largura}`} />
            <Barra className="h-2 w-1/2" />
          </span>
          <Barra className="h-3 w-12" />
        </span>
      ))}
    </Carregando>
  ) : movimentos.isError ? (
    <EstadoVazio
      titulo="Não foi possível carregar os movimentos"
      descricao={explicarFalha(movimentos.error).comoResolver}
      acao={
        <Botao
          nivel="secundario"
          onClick={() => {
            void movimentos.refetch();
          }}
        >
          Carregar de novo
        </Botao>
      }
    />
  ) : linhas.length === 0 ? (
    <EstadoVazio
      titulo="Nenhum movimento neste depósito"
      descricao="O item ainda não entrou nem saiu daqui. A primeira entrada, com a nota, aparece nesta lista."
    />
  ) : (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col" aria-label={`Movimentos de ${saldo.produto} em ${saldo.local}`}>
        {linhas.map((l) => (
          <li key={l.chave} className="flex items-start justify-between gap-4 border-b border-borda-sutil py-2">
            <span className="flex min-w-0 flex-col">
              <span className="txt-corpo text-tinta">{l.titulo}</span>
              <span className="txt-apoio text-tinta-secundaria">
                {[l.detalhe, formatarDataHora(new Date(l.quando))].filter(Boolean).join(' · ')}
              </span>
            </span>
            <span className="txt-corpo shrink-0 whitespace-nowrap text-right tabular-nums text-tinta">
              {comSinal(l.quantidade)} <span className="txt-apoio text-tinta-secundaria">{saldo.unidade}</span>
            </span>
          </li>
        ))}
      </ul>
      {(movimentos.data?.length ?? 0) > LIMITE_DO_LIVRO ? (
        <p className="txt-apoio text-tinta-secundaria">
          {`Mostrando os ${formatarInteiro(LIMITE_DO_LIVRO)} mais recentes de ${formatarInteiro(movimentos.data?.length ?? 0)} movimentos.`}
        </p>
      ) : null}
    </div>
  );

  return (
    <PainelDetalhe
      codigo={saldo.produtoCodigo}
      titulo={saldo.produto}
      subtitulo={saldo.localTipo === 'tanque' ? `Tanque ${saldo.local}` : saldo.local}
      dadosChave={
        <>
          <DadoChave rotulo="Físico">
            <Medida valor={formatarDecimal(saldo.fisico, 1)} unidade={saldo.unidade} />
          </DadoChave>
          <DadoChave rotulo="Reservado">
            <Medida valor={formatarDecimal(saldo.reservado, 1)} unidade={saldo.unidade} />
          </DadoChave>
          <DadoChave rotulo="Disponível">
            <MedidaDeAlerta valor={formatarDecimal(saldo.disponivel, 1)} unidade={saldo.unidade} alerta={saldo.abaixoDoMinimo} />
          </DadoChave>
          <DadoChave rotulo="Situação">{situacaoDoSaldo(saldo)}</DadoChave>
        </>
      }
      abas={[
        /* R06 (p.71): Saldo · Movimentos · Unidades · Aplicações. O Saldo é a
           posição do item NESTE local, por extenso; os dados-chave acima
           são o resumo dela. */
        {
          id: 'saldo',
          rotulo: 'Saldo',
          /* Na grade de dados do painel (pedido do dono, 31/10/2026): duas
             colunas no painel estreito, quatro na janela larga em que ele abre. */
          conteudo: (
            <div className="grade-de-dados">
              <DadoChave rotulo={saldo.localTipo === 'tanque' ? 'Tanque' : 'Depósito'}>{saldo.local}</DadoChave>
              <DadoChave rotulo="Endereço">{saldo.endereco ?? 'Sem endereço'}</DadoChave>
              <DadoChave rotulo="Categoria">{ROTULO_DA_CATEGORIA[saldo.categoria] ?? saldo.categoria}</DadoChave>
              <DadoChave rotulo="Item crítico">{saldo.critico ? 'Sim' : 'Não'}</DadoChave>
              <DadoChave rotulo="Mínimo">
                {saldo.estoqueMinimo > 0 ? <Medida valor={formatarDecimal(saldo.estoqueMinimo, 1)} unidade={saldo.unidade} /> : 'Sem mínimo'}
              </DadoChave>
              <DadoChave rotulo="Físico">
                <Medida valor={formatarDecimal(saldo.fisico, 1)} unidade={saldo.unidade} />
              </DadoChave>
              <DadoChave rotulo="Reservado">
                <Medida valor={formatarDecimal(saldo.reservado, 1)} unidade={saldo.unidade} />
              </DadoChave>
              <DadoChave rotulo="Disponível">
                <MedidaDeAlerta valor={formatarDecimal(saldo.disponivel, 1)} unidade={saldo.unidade} alerta={saldo.abaixoDoMinimo} />
              </DadoChave>
              <DadoChave rotulo="Último movimento">
                {saldo.ultimoMovimentoEm ? formatarDataHora(new Date(saldo.ultimoMovimentoEm)) : 'Nenhum'}
              </DadoChave>
            </div>
          ),
        },
        { id: 'movimentos', rotulo: 'Movimentos', conteudo: listaDeMovimentos },
        /* 0088 · o cadastro do item que o v6 pede: as unidades em que ele chega e
           as máquinas em que ele serve. O combustível do tanque não tem nenhuma das duas. */
        ...(doDeposito
          ? [
              {
                id: 'unidades',
                rotulo: 'Unidades',
                conteudo: (
                  <AbaDasUnidades
                    produtoId={saldo.produtoId}
                    codigo={saldo.produtoCodigo}
                    nome={saldo.produto}
                    unidade={saldo.unidade}
                    podeGerir={podeGerir}
                  />
                ),
              },
            ]
          : []),
        ...(CATEGORIAS_COM_APLICACAO.includes(saldo.categoria)
          ? [
              {
                id: 'aplicacoes',
                rotulo: 'Aplicações',
                conteudo: <AbaDasAplicacoes produtoId={saldo.produtoId} nome={saldo.produto} podeValidar={podeValidar} />,
              },
            ]
          : []),
      ]}
      aoFechar={aoFechar}
    />
  );
}

/* ========================================================================
 * A NECESSIDADE DAS PREVENTIVAS
 * ===================================================================== */

/**
 * O que as preventivas vencidas e as que vencem logo vão pedir, pela lista
 * técnica do plano, contra o disponível de todos os depósitos. É a pergunta da
 * ferramenta 3 do módulo 05: "vai faltar material quando a máquina parar?".
 */
function NecessidadeDasPreventivas() {
  const necessidade = useNecessidade();
  const itens = necessidade.data ?? [];
  if (necessidade.isPending) {
    /* Carregando (p.32): o título é certo; a frase e as linhas, na forma do
       item abaixo (duas linhas à esquerda, o número à direita), são
       esqueleto. Se nenhuma preventiva pedir material, a seção sai quando a
       resposta chega, como sempre saiu. */
    return (
      <section className="cartao flex flex-col gap-3 p-6">
        <div className="flex flex-col gap-1">
          <h2 className="txt-secao text-tinta">Para as preventivas que vencem</h2>
          <Barra className="my-1 h-3 w-1/2" />
        </div>
        <Carregando oQue="Carregando o material das preventivas">
          <ul aria-hidden="true" className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2 last:border-b-0">
                <span className="flex min-w-0 flex-1 flex-col gap-2 py-1">
                  <Barra className={`h-3 ${i === 1 ? 'w-2/5' : 'w-1/2'}`} />
                  <Barra className={`h-3 ${i === 2 ? 'w-3/5' : 'w-3/4'}`} />
                </span>
                <span className="flex flex-col items-end gap-2 py-1">
                  <Barra className="h-3 w-16" />
                  <Barra className="h-3 w-12" />
                </span>
              </li>
            ))}
          </ul>
        </Carregando>
      </section>
    );
  }
  if (itens.length === 0) return null;
  const faltando = itens.filter((n) => n.falta > 0);
  return (
    <section className="cartao flex flex-col gap-3 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Para as preventivas que vencem</h2>
        <p className="txt-apoio text-tinta-secundaria">
          {faltando.length === 0
            ? 'O disponível cobre a lista técnica de toda preventiva vencida ou vencendo.'
            : `${formatarInteiro(faltando.length)} ${faltando.length === 1 ? 'item vai' : 'itens vão'} faltar para as preventivas vencidas ou vencendo.`}
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {itens.map((n) => (
          <li key={n.produtoId} className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2 last:border-b-0">
            <span className="flex flex-col">
              <span className={`txt-corpo text-tinta ${n.falta > 0 ? 'font-semibold' : ''}`}>
                {`${n.produto}${n.critico ? ' · crítico' : ''}`}
              </span>
              <span className="txt-apoio text-tinta-secundaria">
                {`${formatarInteiro(n.tarefas)} ${n.tarefas === 1 ? 'tarefa' : 'tarefas'} em ${formatarInteiro(n.maquinas)} ${n.maquinas === 1 ? 'máquina' : 'máquinas'} · disponível `}
                {/* Número e unidade separados (p.12): o número no tom do texto,
                    com dígitos de largura fixa; a unidade em cinza. No tamanho
                    da linha — a `Medida` levaria o número a 14 px numa segunda
                    linha de 12. */}
                <span className="whitespace-nowrap">
                  <span className="tabular-nums text-tinta">{formatarDecimal(n.disponivel, 1)}</span>
                  {` ${n.unidade}`}
                </span>
              </span>
            </span>
            <span className="flex flex-col items-end">
              {/* Unidade em cinza, separada do número (p.12). */}
              <Medida valor={formatarDecimal(n.necessario, 1)} unidade={n.unidade} />
              {n.falta > 0 ? (
                /* R02: a falta é o próprio alerta — em vermelho, e escrita. */
                <span className="txt-apoio text-data-vencida">
                  {`faltam ${formatarDecimal(n.falta, 1)}`}
                  <span className="text-tinta-secundaria">{` ${n.unidade}`}</span>
                </span>
              ) : (
                <span className="txt-apoio text-tinta-secundaria">coberto</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ========================================================================
 * LANÇAR MOVIMENTO
 * ===================================================================== */

/** O tipo em segmentado (p.24: 2 a 4 itens), um toque, e a frase do que ele é logo abaixo. */
const ROTULO_DO_TIPO: Readonly<Record<TipoDeMovimento, string>> = {
  entrada: 'Entrada',
  saida: 'Saída',
  devolucao: 'Devolução',
  transferencia: 'Transferência',
};
const AJUDA_DO_TIPO: Readonly<Record<TipoDeMovimento, string>> = {
  entrada: 'Entrada com nota fiscal ou romaneio.',
  saida: 'Saída para uma O.S. ou uma máquina.',
  devolucao: 'Peça que voltou de uma O.S.',
  transferencia: 'De um depósito para outro.',
};

type CampoDoMovimento = 'produto' | 'deposito' | 'quantidade' | 'documento' | 'destino' | 'ordem';

/**
 * A UNIDADE AO LADO DO NÚMERO, em cinza (p.23), como os litros do
 * abastecimento: quem digita "12" vê "un" ou "CX 12" sem ler o rótulo.
 */
function ComUnidade({ unidade, children }: { readonly unidade: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      {children}
      {unidade ? <span className="txt-corpo shrink-0 text-tinta-apagada">{unidade}</span> : null}
    </div>
  );
}

/**
 * LANÇAR MOVIMENTO, em duas camadas (pedido do dono, 31/10/2026: as melhorias
 * do abastecimento levadas aos outros formulários).
 *
 *  - **À vista, o que se digita todo dia**: o tipo num toque, o item, a
 *    quantidade e o que o tipo pede (a nota e o custo na entrada; a O.S. ou a
 *    máquina na saída; o destino na transferência).
 *  - **O que o sistema já sabe vira etiqueta**: o depósito (o único, o último
 *    usado, ou o que tem o item), a unidade do item e, na entrada, o
 *    fornecedor da última nota do item. Os campos abrem em "Ajustar …".
 *
 * O custo da entrada já vem com o da última entrada do item, e a ajuda diz
 * de onde ("Último custo: R$ 12,40 em 20/10/2026") e o total da nota a cada
 * tecla. Nenhum campo saiu e nenhuma regra mudou: o servidor recusa o que
 * recusava, e o que o sistema propôs se troca no campo.
 */
function FormularioDeMovimento({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const apoio = useApoioDoEstoque();
  const saldos = useSaldos();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const movimentar = useMovimentar();
  const podeCadastrarDeposito = useSessao().data?.permissoes.includes('estoque.gerir') ?? false;
  const [ultimoDeposito, guardarDeposito] = useUltimoUsado('estoque.deposito');
  const quantosDepositos = apoio.data?.depositos.length ?? 0;
  const [tipo, setTipo] = useState<TipoDeMovimento>('entrada');
  const [produto, setProduto] = useState('');
  /* `null` = segue o que o sistema propõe; o texto é a escolha da pessoa. */
  const [depositoEscolhido, setDepositoEscolhido] = useState<string | null>(null);
  const [destino, setDestino] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [documento, setDocumento] = useState('');
  const [custoDigitado, setCustoDigitado] = useState<string | null>(null);
  const [fornecedorEscolhido, setFornecedorEscolhido] = useState<string | null>(null);
  const [ordem, setOrdem] = useState('');
  const [maquina, setMaquina] = useState('');
  /* 0088 · a embalagem em que a quantidade foi contada; vazio é a unidade do item. */
  const [conversaoId, setConversaoId] = useState('');
  const [ajustando, setAjustando] = useState(false);
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const itemEscolhido = apoio.data?.produtos.find((p) => p.id === produto) ?? null;
  const conversoes = itemEscolhido?.conversoes ?? [];
  const conversao = conversoes.find((c) => c.id === conversaoId) ?? null;
  const unidadeDoCampo = conversao?.unidade ?? itemEscolhido?.unidade ?? '';
  const ehEntrada = tipo === 'entrada';

  /* O livro do item: a última entrada dá o custo e o fornecedor. Só com o item escolhido. */
  const livroDoItem = useQuery({
    queryKey: ['estoque', 'movimentos', produto, null],
    enabled: produto !== '',
    queryFn: async () => (await api.movimentos({ produtoId: produto })).movimentos,
  });
  const daUltimaEntrada = useMemo(() => ultimaEntrada(livroDoItem.data ?? []), [livroDoItem.data]);

  const sugestaoDoDeposito = useMemo(
    () =>
      apoio.data
        ? depositoSugerido({
            tipo,
            produtoId: produto,
            depositos: apoio.data.depositos,
            saldos: saldos.data ?? [],
            ultimo: ultimoDeposito,
            ...(itemEscolhido ? { unidade: itemEscolhido.unidade } : {}),
          })
        : null,
    [apoio.data, saldos.data, tipo, produto, ultimoDeposito, itemEscolhido],
  );
  const deposito = depositoEscolhido ?? sugestaoDoDeposito?.valor ?? '';
  const depositoDoSistema = depositoEscolhido === null && sugestaoDoDeposito !== null;

  const sugestaoDoCusto = ehEntrada && itemEscolhido ? custoSugerido(daUltimaEntrada.custo, itemEscolhido.unidade, conversao) : null;
  const custo = custoDigitado ?? sugestaoDoCusto?.valor ?? '';
  const custoDoSistema = custoDigitado === null && sugestaoDoCusto !== null;

  const fornecedorDaNota =
    daUltimaEntrada.fornecedor && apoio.data?.fornecedores.some((f) => f.id === daUltimaEntrada.fornecedor?.id)
      ? daUltimaEntrada.fornecedor
      : null;
  const fornecedor = fornecedorEscolhido ?? fornecedorDaNota?.id ?? '';
  const fornecedorDoSistema = fornecedorEscolhido === null && fornecedorDaNota !== null;

  /* O que o depósito tem do item: a ajuda da quantidade na saída. */
  const posicao = (saldos.data ?? []).find((s) => s.localTipo === 'deposito' && s.produtoId === produto && s.localId === deposito) ?? null;
  const nomeDoDeposito = apoio.data?.depositos.find((d) => d.id === deposito)?.nome ?? null;
  const nomeDoFornecedor = apoio.data?.fornecedores.find((f) => f.id === fornecedor)?.nome ?? null;
  const total = ehEntrada ? totalDaLinha(interpretarNumero(quantidade), custo.trim() === '' ? null : interpretarNumero(custo)) : null;
  const ordemEscolhida = apoio.data?.ordens.find((o) => o.id === ordem) ?? null;

  /* O depósito mora na segunda camada quando o sistema tem a resposta; sem
     resposta, ele fica à vista, junto do item. */
  const depositoNaEtiqueta = sugestaoDoDeposito !== null;

  /* As mesmas regras de sempre, agora uma por campo: a frase vai abaixo do
     campo que ela descreve (p.16), e não num bloco no pé do formulário. */
  function checar(campo: CampoDoMovimento): string | null {
    if (campo === 'produto') return produto === '' ? 'Escolha o item.' : null;
    if (campo === 'deposito') return deposito === '' ? faltaDoDeposito(quantosDepositos, podeCadastrarDeposito) : null;
    if (campo === 'quantidade') {
      const q = interpretarNumero(quantidade);
      return q === null || q <= 0 ? 'Informe a quantidade, maior que zero.' : null;
    }
    if (campo === 'documento') {
      return tipo === 'entrada' && documento.trim() === '' ? 'A entrada precisa do documento: nota fiscal ou romaneio.' : null;
    }
    if (campo === 'destino') return tipo === 'transferencia' && destino === '' ? 'Escolha o depósito de destino.' : null;
    if (tipo === 'saida' && ordem === '' && maquina === '') return 'Diga para onde a peça vai: uma O.S. ou uma máquina.';
    if (tipo === 'devolucao' && ordem === '') return 'Diga de qual O.S. a peça voltou.';
    return null;
  }

  function sair(campo: CampoDoMovimento) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }
  function limpar(campo: CampoDoMovimento) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      produto: checar('produto'),
      deposito: checar('deposito'),
      quantidade: checar('quantidade'),
      documento: checar('documento'),
      destino: checar('destino'),
      ordem: checar('ordem'),
    };
    setErros(achados);
    /* O erro do depósito que mora na segunda camada abre a camada: erro
       escondido atrás de um botão é erro que ninguém acha. */
    if (achados.deposito && depositoNaEtiqueta) setAjustando(true);
    const q = interpretarNumero(quantidade);
    if (temErro(achados) || q === null) return;
    setRecusa(null);
    movimentar.mutate(
      {
        tipo,
        produtoId: produto,
        depositoId: deposito,
        depositoDestinoId: tipo === 'transferencia' ? destino : null,
        quantidade: q,
        conversaoId: conversao?.id ?? null,
        documento: documento.trim() || null,
        custoUnitario: !ehEntrada || custo.trim() === '' ? null : interpretarNumero(custo),
        fornecedorId: ehEntrada && fornecedor !== '' ? fornecedor : null,
        ordemId: ordem || null,
        equipamentoId: maquina || null,
      },
      {
        onSuccess: () => {
          guardarDeposito(deposito);
          aoFechar();
        },
        onError: (falha) => { setRecusa(explicarFalha(falha)); },
      },
    );
  }

  const campoDoDeposito = (
    <Campo
      id={`${base}-dep`}
      rotulo={tipo === 'entrada' || tipo === 'devolucao' ? 'Entra no depósito' : 'Sai do depósito'}
      preenchido={depositoDoSistema}
      erro={erros['deposito'] ?? null}
      {...(depositoDoSistema && sugestaoDoDeposito ? { ajuda: sugestaoDoDeposito.ajuda } : {})}
    >
      {apoio.isPending ? (
        <ControleCarregando oQue="Carregando os depósitos" />
      ) : (
        <ListaDeEscolha
          id={`${base}-dep`}
          rotulo="Depósito"
          valor={deposito}
          aoMudar={(valor) => { setDepositoEscolhido(valor); limpar('deposito'); }}
          aoSair={() => { sair('deposito'); }}
          rotuloVazio={opcaoVaziaDoDeposito(quantosDepositos, podeCadastrarDeposito)}
          opcoes={(apoio.data?.depositos ?? []).map((d) => ({ valor: d.id, rotulo: d.nome, etiquetas: [{ texto: d.codigo, tom: 'informacao' as const, titulo: 'Código' }] }))}
          idDoErro={erros['deposito'] ? `${base}-dep-erro` : undefined}
        />
      )}
    </Campo>
  );

  /* As listas de item, depósito, O.S. e máquina vêm do apoio: enquanto ele
     não chega, cada uma é a barra do controle (p.32), e não uma lista que só
     deixa escolher o nada. */
  const opcoes = apoio.data;
  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-6">
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        O movimento não se edita: se um lançamento sair errado, a correção é um ajuste com o motivo escrito. A
        peça aplicada numa O.S. se lança pela própria O.S., que já baixa o estoque.
      </p>
      <div className="formulario-grade">
        <div className="campo-linha flex min-w-0 flex-col gap-2">
          <span className="txt-rotulo-campo">Tipo</span>
          <Seletor
            larguraCheia
            rotulo="Tipo do movimento"
            valor={tipo}
            opcoes={(Object.keys(ROTULO_DO_TIPO) as TipoDeMovimento[]).map((t) => ({ valor: t, rotulo: ROTULO_DO_TIPO[t] }))}
            aoEscolher={(novo) => {
              setTipo(novo);
              /* Os campos que dependem do tipo mudam: o erro deles sai junto. */
              setErros((atual) => ({ ...atual, documento: null, destino: null, ordem: null }));
            }}
          />
          <span className="txt-ajuda">{AJUDA_DO_TIPO[tipo]}</span>
        </div>
        <div className="campo-largo">
          <Campo id={`${base}-produto`} rotulo="Item" erro={erros['produto'] ?? null}>
            {apoio.isPending ? (
              <ControleCarregando oQue="Carregando os itens" />
            ) : (
              <ListaDeEscolha
                id={`${base}-produto`}
                rotulo="Item"
                valor={produto}
                aoMudar={(valor) => {
                  setProduto(valor);
                  setConversaoId('');
                  /* Outro item, outra última nota: o custo e o fornecedor voltam a ser os do sistema. */
                  setCustoDigitado(null);
                  setFornecedorEscolhido(null);
                  limpar('produto');
                }}
                aoSair={() => { sair('produto'); }}
                rotuloVazio="Escolha o item"
                opcoes={(opcoes?.produtos ?? []).map((p) => opcaoDoItem(p))}
                idDoErro={erros['produto'] ? `${base}-produto-erro` : undefined}
              />
            )}
          </Campo>
        </div>
        {depositoNaEtiqueta ? null : campoDoDeposito}
        <Campo
          id={`${base}-qtd`}
          rotulo="Quantidade"
          erro={erros['quantidade'] ?? null}
          {...(conversao && itemEscolhido
            ? { ajuda: ajudaDaConversao(interpretarNumero(quantidade), conversao, itemEscolhido.unidade) }
            : (tipo === 'saida' || tipo === 'transferencia') && itemEscolhido && nomeDoDeposito
              ? { ajuda: `Disponível em ${nomeDoDeposito}: ${formatarDecimal(posicao?.disponivel ?? 0, 1)} ${itemEscolhido.unidade}.` }
              : {})}
        >
          <ComUnidade unidade={unidadeDoCampo}>
            <input
              id={`${base}-qtd`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              value={quantidade}
              onChange={(e) => { setQuantidade(e.target.value); limpar('quantidade'); }}
              onBlur={() => { sair('quantidade'); }}
              {...marcaDeErro(erros['quantidade'], `${base}-qtd`)}
            />
          </ComUnidade>
        </Campo>
        {tipo === 'transferencia' ? (
          <Campo id={`${base}-destino`} rotulo="Vai para o depósito" erro={erros['destino'] ?? null}>
            {apoio.isPending ? (
              <ControleCarregando oQue="Carregando os depósitos" />
            ) : (
              <ListaDeEscolha
                id={`${base}-destino`}
                rotulo="Vai para o depósito"
                valor={destino}
                aoMudar={(valor) => { setDestino(valor); limpar('destino'); }}
                aoSair={() => { sair('destino'); }}
                rotuloVazio="Escolha o destino"
                opcoes={(opcoes?.depositos ?? []).filter((d) => d.id !== deposito).map((d) => ({ valor: d.id, rotulo: d.nome, etiquetas: [{ texto: d.codigo, tom: 'informacao' as const, titulo: 'Código' }] }))}
                idDoErro={erros['destino'] ? `${base}-destino-erro` : undefined}
              />
            )}
          </Campo>
        ) : null}
        {ehEntrada ? (
          <>
            <Campo id={`${base}-doc`} rotulo="Nota fiscal ou romaneio" erro={erros['documento'] ?? null}>
              <input
                id={`${base}-doc`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                placeholder="NF 4411"
                value={documento}
                onChange={(e) => { setDocumento(e.target.value); limpar('documento'); }}
                onBlur={() => { sair('documento'); }}
                {...marcaDeErro(erros['documento'], `${base}-doc`)}
              />
            </Campo>
            <Campo
              id={`${base}-custo`}
              rotulo={conversao ? `Custo por ${conversao.unidade}` : 'Custo unitário'}
              opcional
              preenchido={custoDoSistema}
              {...(() => {
                const partes = [custoDoSistema ? sugestaoDoCusto?.ajuda : null, total === null ? null : `Total: ${formatarReais(total)}.`].filter(Boolean);
                return partes.length > 0 ? { ajuda: partes.join(' ') } : {};
              })()}
            >
              <ComUnidade unidade="R$">
                <input
                  id={`${base}-custo`}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="controle w-full"
                  value={custo}
                  onChange={(e) => { setCustoDigitado(e.target.value); }}
                />
              </ComUnidade>
            </Campo>
          </>
        ) : null}
        {tipo === 'saida' || tipo === 'devolucao' ? (
          <>
            <Campo
              id={`${base}-os`}
              rotulo="O.S."
              opcional={tipo === 'saida'}
              erro={erros['ordem'] ?? null}
              {...(ordemEscolhida ? { ajuda: `Máquina da O.S.: ${ordemEscolhida.equipamento}.` } : {})}
            >
              {apoio.isPending ? (
                <ControleCarregando oQue="Carregando as O.S." />
              ) : (
                <ListaDeEscolha
                  id={`${base}-os`}
                  rotulo="O.S."
                  valor={ordem}
                  aoMudar={(valor) => { setOrdem(valor); limpar('ordem'); }}
                  aoSair={() => { sair('ordem'); }}
                  rotuloVazio={tipo === 'saida' ? 'Nenhuma' : 'Escolha a O.S.'}
                  opcaoUnica={false}
                  opcoes={(opcoes?.ordens ?? []).map((o) => ({ valor: o.id, rotulo: numeroDaOrdem(o.numero), detalhe: o.equipamento }))}
                  idDoErro={erros['ordem'] ? `${base}-os-erro` : undefined}
                />
              )}
            </Campo>
            {tipo === 'saida' ? (
              <Campo id={`${base}-maq`} rotulo="Máquina" opcional>
                {apoio.isPending ? (
                  <ControleCarregando oQue="Carregando as máquinas" />
                ) : (
                  <ListaDeEscolha
                    id={`${base}-maq`}
                    rotulo="Máquina"
                    valor={maquina}
                    aoMudar={(valor) => { setMaquina(valor); limpar('ordem'); }}
                    rotuloVazio="Nenhuma"
                    opcoes={comVisualDaFrota((opcoes?.equipamentos ?? []).map((m) => opcaoDaMaquina(m)))}
                    adesivo
                  />
                )}
              </Campo>
            ) : null}
          </>
        ) : null}

        {/* ---- O que o sistema já sabe, em etiqueta; os campos atrás do botão. ---- */}
        {depositoNaEtiqueta || conversoes.length > 0 || ehEntrada ? (
          <LinhaDoPreenchido
            rotulo={ehEntrada ? 'Onde e de quem' : 'Onde'}
            aberta={ajustando}
            aoAlternar={() => { setAjustando((aberto) => !aberto); }}
            rotuloDoBotao={[depositoNaEtiqueta ? 'depósito' : null, conversoes.length > 0 ? 'unidade' : null, ehEntrada ? 'fornecedor' : null]
              .filter(Boolean)
              .join(', ')
              .replace(/, ([^,]*)$/, ' e $1')}
            idDoConteudo={`${base}-ajustes`}
            aviso={erros['deposito'] && depositoNaEtiqueta && !ajustando ? erros['deposito'] : null}
          >
            {depositoNaEtiqueta ? (
              <EtiquetaDoPreenchido rotulo="Depósito" valor={nomeDoDeposito ?? 'Sem depósito'} vazia={nomeDoDeposito === null} marcada={depositoDoSistema} />
            ) : null}
            {conversoes.length > 0 && itemEscolhido ? (
              <EtiquetaDoPreenchido rotulo="Unidade" valor={conversao ? textoDaConversao(conversao, itemEscolhido.unidade) : itemEscolhido.unidade} />
            ) : null}
            {ehEntrada ? (
              <EtiquetaDoPreenchido rotulo="Fornecedor" valor={nomeDoFornecedor ?? 'Sem fornecedor'} vazia={nomeDoFornecedor === null} marcada={fornecedorDoSistema} />
            ) : null}
          </LinhaDoPreenchido>
        ) : null}
        {ajustando ? (
          <div id={`${base}-ajustes`} className="campo-linha formulario-grade bloco-rebaixado p-4">
            {depositoNaEtiqueta ? campoDoDeposito : null}
            {conversoes.length > 0 && itemEscolhido ? (
              /* 0088 · o item chega em caixa ou balde: a quantidade pode ser na
                 embalagem, e o banco converte para a unidade do item. */
              <Campo id={`${base}-un`} rotulo="Unidade">
                <ListaDeEscolha
                  id={`${base}-un`}
                  rotulo="Unidade"
                  valor={conversaoId}
                  aoMudar={(valor) => { setConversaoId(valor); }}
                  rotuloVazio={`${itemEscolhido.unidade} (unidade do item)`}
                  opcaoUnica={false}
                  opcoes={conversoes.map((c) => ({ valor: c.id, rotulo: textoDaConversao(c, itemEscolhido.unidade) }))}
                />
              </Campo>
            ) : null}
            {ehEntrada ? (
              <Campo
                id={`${base}-forn`}
                rotulo="Fornecedor"
                opcional
                preenchido={fornecedorDoSistema}
                {...(fornecedorDoSistema && fornecedorDaNota
                  ? { ajuda: `Veio da última entrada do item, em ${formatarData(new Date(fornecedorDaNota.em))}.` }
                  : {})}
              >
                {apoio.isPending ? (
                  <ControleCarregando oQue="Carregando os fornecedores" />
                ) : (
                  <ListaDeEscolha
                    id={`${base}-forn`}
                    rotulo="Fornecedor"
                    valor={fornecedor}
                    aoMudar={(valor) => { setFornecedorEscolhido(valor); }}
                    rotuloVazio="Sem fornecedor"
                    opcoes={(opcoes?.fornecedores ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))}
                  />
                )}
              </Campo>
            ) : null}
          </div>
        ) : null}
      </div>
      <Aviso recusa={recusa} />
      {/* Primário à direita, o de sair como terciário à esquerda (p.15, p.29). */}
      <div className="barra-de-acoes">
        <Botao nivel="terciario" type="button" onClick={aoFechar}>
          Descartar lançamento
        </Botao>
        <Botao nivel="primario" type="submit" disabled={movimentar.isPending}>
          Lançar movimento
        </Botao>
      </div>
    </form>
  );
}

/* ========================================================================
 * CONTAGEM DE INVENTÁRIO
 * ===================================================================== */

type CampoDaContagem = 'produto' | 'deposito' | 'contado';

/**
 * A contagem traz o depósito (o único, o último usado ou onde o item já
 * está) e diz, a cada tecla, o que o livro tem e o ajuste que vai nascer. A
 * observação é rara: fica atrás de um botão.
 */
function FormularioDeContagem({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const apoio = useApoioDoEstoque();
  const saldos = useSaldos();
  const contar = useContar();
  const podeCadastrarDeposito = useSessao().data?.permissoes.includes('estoque.gerir') ?? false;
  const [ultimoDeposito, guardarDeposito] = useUltimoUsado('estoque.deposito');
  const quantosDepositos = apoio.data?.depositos.length ?? 0;
  const [produto, setProduto] = useState('');
  const [depositoEscolhido, setDepositoEscolhido] = useState<string | null>(null);
  const [contado, setContado] = useState('');
  const [observacao, setObservacao] = useState('');
  const [comObservacao, setComObservacao] = useState(false);
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const itemEscolhido = apoio.data?.produtos.find((p) => p.id === produto) ?? null;

  const sugestaoDoDeposito = useMemo(
    () =>
      apoio.data
        ? depositoSugerido({ tipo: 'contagem', produtoId: produto, depositos: apoio.data.depositos, saldos: saldos.data ?? [], ultimo: ultimoDeposito })
        : null,
    [apoio.data, saldos.data, produto, ultimoDeposito],
  );
  const deposito = depositoEscolhido ?? sugestaoDoDeposito?.valor ?? '';
  const depositoDoSistema = depositoEscolhido === null && sugestaoDoDeposito !== null;
  const noLivro =
    produto === '' || deposito === ''
      ? null
      : ((saldos.data ?? []).find((s) => s.localTipo === 'deposito' && s.produtoId === produto && s.localId === deposito)?.fisico ?? 0);
  const fraseDoLivro = itemEscolhido ? fraseDaContagem(noLivro, contado.trim() === '' ? null : interpretarNumero(contado), itemEscolhido.unidade) : null;

  function checar(campo: CampoDaContagem): string | null {
    if (campo === 'produto') return produto === '' ? 'Escolha o item contado.' : null;
    if (campo === 'deposito') return deposito === '' ? faltaDoDeposito(quantosDepositos, podeCadastrarDeposito, 'Escolha o depósito onde foi contado.') : null;
    const q = interpretarNumero(contado);
    return q === null || q < 0 ? 'Informe quanto foi contado, zero inclusive.' : null;
  }

  function sair(campo: CampoDaContagem) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }
  function limpar(campo: CampoDaContagem) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { produto: checar('produto'), deposito: checar('deposito'), contado: checar('contado') };
    setErros(achados);
    const q = interpretarNumero(contado);
    if (temErro(achados) || q === null) return;
    setRecusa(null);
    contar.mutate(
      { produtoId: produto, depositoId: deposito, contado: q, observacao: observacao.trim() || null },
      {
        /* O retorno é o toast (R04, p.32) que `useContar` declara — com o
           ajuste, quando houve; a janela fecha, como a do movimento. */
        onSuccess: () => {
          guardarDeposito(deposito);
          aoFechar();
        },
        onError: (falha) => { setRecusa(explicarFalha(falha)); },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-6">
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        Informe o que foi contado na prateleira. A diferença para o saldo vira um ajuste, com as duas contas
        escritas no motivo. O saldo nunca é sobrescrito sem deixar rastro.
      </p>
      <div className="formulario-grade">
        <div className="campo-largo">
          <Campo id={`${base}-produto`} rotulo="Item" erro={erros['produto'] ?? null}>
            {apoio.isPending ? (
              <ControleCarregando oQue="Carregando os itens" />
            ) : (
              <ListaDeEscolha
                id={`${base}-produto`}
                rotulo="Item"
                valor={produto}
                aoMudar={(valor) => { setProduto(valor); limpar('produto'); }}
                aoSair={() => { sair('produto'); }}
                rotuloVazio="Escolha o item"
                opcoes={(apoio.data?.produtos ?? []).map((p) => opcaoDoItem(p))}
                idDoErro={erros['produto'] ? `${base}-produto-erro` : undefined}
              />
            )}
          </Campo>
        </div>
        <Campo
          id={`${base}-dep`}
          rotulo="Depósito"
          preenchido={depositoDoSistema}
          erro={erros['deposito'] ?? null}
          {...(depositoDoSistema && sugestaoDoDeposito ? { ajuda: sugestaoDoDeposito.ajuda } : {})}
        >
          {apoio.isPending ? (
            <ControleCarregando oQue="Carregando os depósitos" />
          ) : (
            <ListaDeEscolha
              id={`${base}-dep`}
              rotulo="Depósito"
              valor={deposito}
              aoMudar={(valor) => { setDepositoEscolhido(valor); limpar('deposito'); }}
              aoSair={() => { sair('deposito'); }}
              rotuloVazio={opcaoVaziaDoDeposito(quantosDepositos, podeCadastrarDeposito)}
              opcoes={(apoio.data?.depositos ?? []).map((d) => ({ valor: d.id, rotulo: d.nome, etiquetas: [{ texto: d.codigo, tom: 'informacao' as const, titulo: 'Código' }] }))}
              idDoErro={erros['deposito'] ? `${base}-dep-erro` : undefined}
            />
          )}
        </Campo>
        <Campo id={`${base}-contado`} rotulo="Contado" erro={erros['contado'] ?? null} {...(fraseDoLivro ? { ajuda: fraseDoLivro } : {})}>
          <ComUnidade unidade={itemEscolhido?.unidade ?? ''}>
            <input
              id={`${base}-contado`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              value={contado}
              onChange={(e) => { setContado(e.target.value); limpar('contado'); }}
              onBlur={() => { sair('contado'); }}
              {...marcaDeErro(erros['contado'], `${base}-contado`)}
            />
          </ComUnidade>
        </Campo>
        <BotaoDoRaro
          aberto={comObservacao}
          aoAlternar={() => { setComObservacao((aberto) => !aberto || observacao.trim() !== ''); }}
          rotulo="Escrever observação"
          rotuloAberto="Fechar observação"
          idDoConteudo={`${base}-obs`}
        />
        {comObservacao ? (
          <div className="campo-linha">
            <Campo id={`${base}-obs`} rotulo="Observação" opcional>
              <input id={`${base}-obs`} type="text" autoComplete="off" className="controle w-full" placeholder="Caixa achada na prateleira B" value={observacao} onChange={(e) => { setObservacao(e.target.value); }} />
            </Campo>
          </div>
        ) : null}
      </div>
      <Aviso recusa={recusa} />
      {/* Rótulo verbo + objeto que diz o que acontece (p.15): "Fechar
          contagem" se lia como encerrar o inventário. */}
      <div className="barra-de-acoes">
        <Botao nivel="terciario" type="button" onClick={aoFechar}>
          Descartar contagem
        </Botao>
        <Botao nivel="primario" type="submit" disabled={contar.isPending}>
          Registrar contagem
        </Botao>
      </div>
    </form>
  );
}

/* ========================================================================
 * CADASTRAR ITEM E CADASTRAR DEPÓSITO
 * Dois formulários, duas janelas: cada um com a SUA ação principal (p.15) e
 * uma barra de ações só (R01, p.28) — antes eram dois formulários no mesmo
 * cartão, sem primário nenhum e com duas barras fixas uma sobre a outra.
 * ===================================================================== */

/**
 * 0131 · O CODIGO QUE O SISTEMA CRIA (decisao 57, 29/10/2026): o item e o
 * deposito nascem com PREFIXO-NNN na sequencia da empresa, e ninguem o digita.
 * No cadastro ele ainda nao existe: a linha diz quando ele nasce, e o toast da
 * gravacao o escreve.
 */
function CodigoDoSistema() {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="txt-rotulo-campo">Código</span>
      <span className="txt-apoio text-tinta-secundaria">O código é criado pelo sistema ao salvar.</span>
    </div>
  );
}

const CATEGORIAS_DO_ALMOXARIFADO = ['peca', 'filtro', 'pneu', 'insumo', 'lubrificante', 'outro'] as const;
const UNIDADES = ['un', 'L', 'kg', 'm', 'par', 'jogo', 'cx'] as const;

type CampoDoCadastro = 'nome' | 'embalagem' | 'fator';

function FormularioDeItem({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const criarItem = useCriarItem();
  const [nome, setNome] = useState('');
  const [categoria, setCategoria] = useState<string>('peca');
  /* `null` = a unidade da categoria (lubrificante em litro, o resto por
     unidade); o texto é a escolha da pessoa. */
  const [unidadeEscolhida, setUnidadeEscolhida] = useState<string | null>(null);
  const unidade = unidadeEscolhida ?? unidadeDaCategoria(categoria);
  /* O raro — fabricante, código do fabricante e embalagem — atrás de um botão. */
  const [comDetalhes, setComDetalhes] = useState(false);
  const [fabricante, setFabricante] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [critico, setCritico] = useState(false);
  /* 0088 · a embalagem em que o item chega, opcional: vira a primeira
     unidade de compra dele, logo depois do cadastro. */
  const [embalagem, setEmbalagem] = useState('');
  const [fator, setFator] = useState('');
  const criarConversao = useCriarConversao();
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const comEmbalagem = embalagem.trim() !== '' || fator.trim() !== '';

  function checar(campo: CampoDoCadastro): string | null {
    if (campo === 'embalagem') return comEmbalagem ? erroDaEmbalagem(embalagem, unidade) : null;
    if (campo === 'fator') return comEmbalagem ? erroDoFator(fator, unidade) : null;
    return nome.trim() === '' ? 'Informe o nome do item.' : null;
  }

  function sair(campo: CampoDoCadastro) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }
  function limpar(campo: CampoDoCadastro) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function gravarItem(evento: FormEvent) {
    evento.preventDefault();
    const achados = { nome: checar('nome'), embalagem: checar('embalagem'), fator: checar('fator') };
    setErros(achados);
    if (achados.embalagem || achados.fator) setComDetalhes(true);
    if (temErro(achados)) return;
    setRecusa(null);
    const conversao = comEmbalagem ? { unidade: embalagem.trim(), fator: interpretarNumero(fator) ?? 0 } : null;
    criarItem.mutate(
      {
        nome: nome.trim(),
        categoria,
        unidade,
        fabricante: fabricante.trim() || null,
        codigoFabricante: partNumber.trim() || null,
        critico,
      },
      {
        /* O retorno é o toast "Item cadastrado" (R04, p.32) que `useCriarItem`
           declara, e não uma frase dentro da janela (achado dos robôs no QAS,
           20/10/2026): a janela fecha, como a do movimento. A embalagem segue
           depois, com o toast dela. */
        onSuccess: (criado) => {
          /* O codigo do item e o que o sistema criou (0131). */
          if (conversao) criarConversao.mutate({ produtoId: criado.id, ...conversao, doItem: { codigo: criado.codigo, nome: nome.trim(), unidade } });
          aoFechar();
        },
        onError: (falha) => { setRecusa(explicarFalha(falha)); },
      },
    );
  }

  return (
    <form onSubmit={gravarItem} noValidate className="flex flex-col gap-6">
      <div className="formulario-grade">
        {/* 0131 · O CODIGO INTERNO E DO SISTEMA (decisao 57): ele nasce ao
            salvar, na sequencia da categoria (FLT-008), e ninguem o digita. O
            do fabricante continua no campo proprio. */}
        <CodigoDoSistema />
        <div className="campo-largo">
          <Campo id={`${base}-nome`} rotulo="Nome" erro={erros['nome'] ?? null}>
            <input
              id={`${base}-nome`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Filtro de óleo do motor"
              value={nome}
              onChange={(e) => { setNome(e.target.value); limpar('nome'); }}
              onBlur={() => { sair('nome'); }}
              {...marcaDeErro(erros['nome'], `${base}-nome`)}
            />
          </Campo>
        </div>
        <Campo id={`${base}-cat`} rotulo="Categoria">
          <select id={`${base}-cat`} className="controle w-full" value={categoria} onChange={(e) => { setCategoria(e.target.value); }}>
            {CATEGORIAS_DO_ALMOXARIFADO.map((c) => (
              <option key={c} value={c}>
                {ROTULO_DA_CATEGORIA[c]}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          id={`${base}-un`}
          rotulo="Unidade"
          preenchido={unidadeEscolhida === null}
          {...(unidadeEscolhida === null ? { ajuda: `Pela categoria: ${ROTULO_DA_CATEGORIA[categoria] ?? categoria} em ${unidade}.` } : {})}
        >
          <select id={`${base}-un`} className="controle w-full" value={unidade} onChange={(e) => { setUnidadeEscolhida(e.target.value); }}>
            {UNIDADES.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </Campo>
        <label className="flex items-center gap-2 self-end pb-2 txt-corpo text-tinta" htmlFor={`${base}-crit`}>
          <input id={`${base}-crit`} type="checkbox" checked={critico} onChange={(e) => { setCritico(e.target.checked); }} />
          Item crítico
        </label>
        <BotaoDoRaro
          aberto={comDetalhes}
          aoAlternar={() => {
            /* Com o que já foi escrito lá dentro, o bloco não fecha: quem
               escreveu não perde de vista o que escreveu. */
            setComDetalhes((aberto) => !aberto || [fabricante, partNumber, embalagem, fator].some((v) => v.trim() !== ''));
          }}
          rotulo="Fabricante e embalagem de compra"
          rotuloAberto="Fechar fabricante e embalagem"
          idDoConteudo={`${base}-detalhes`}
        />
        {comDetalhes ? (
        <div id={`${base}-detalhes`} className="campo-linha formulario-grade bloco-rebaixado p-4">
        <Campo id={`${base}-fab`} rotulo="Fabricante" opcional>
          <input id={`${base}-fab`} type="text" autoComplete="off" className="controle w-full" value={fabricante} onChange={(e) => { setFabricante(e.target.value); }} />
        </Campo>
        <Campo id={`${base}-pn`} rotulo="Código do fabricante" opcional ajuda="O part number evita a mesma peça com dois códigos internos.">
          <input id={`${base}-pn`} type="text" autoComplete="off" className="controle w-full" value={partNumber} onChange={(e) => { setPartNumber(e.target.value); }} />
        </Campo>
        <Campo
          id={`${base}-emb`}
          rotulo="Embalagem de compra"
          opcional
          erro={erros['embalagem'] ?? null}
          ajuda="Se o item chega em caixa ou balde, como CX 12. A entrada converte para a unidade do item."
        >
          <input
            id={`${base}-emb`}
            type="text"
            autoComplete="off"
            maxLength={40}
            className="controle w-full"
            placeholder="CX 12"
            value={embalagem}
            onChange={(e) => { setEmbalagem(e.target.value); limpar('embalagem'); }}
            onBlur={() => { sair('embalagem'); }}
            {...marcaDeErro(erros['embalagem'], `${base}-emb`)}
          />
        </Campo>
        <Campo id={`${base}-fator`} rotulo={`Quantas ${unidade} cabem nela`} opcional erro={erros['fator'] ?? null}>
          <input
            id={`${base}-fator`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full"
            placeholder="12"
            value={fator}
            onChange={(e) => { setFator(e.target.value); limpar('fator'); }}
            onBlur={() => { sair('fator'); }}
            {...marcaDeErro(erros['fator'], `${base}-fator`)}
          />
        </Campo>
        </div>
        ) : null}
      </div>
      <Aviso recusa={recusa} />
      <div className="barra-de-acoes">
        <Botao nivel="terciario" type="button" onClick={aoFechar}>
          Descartar cadastro
        </Botao>
        <Botao nivel="primario" type="submit" disabled={criarItem.isPending}>
          Cadastrar item
        </Botao>
      </div>
    </form>
  );
}

function FormularioDeDeposito({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const criarDeposito = useCriarDeposito();
  const [depNome, setDepNome] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);

  /* So o nome: o codigo do deposito e do sistema (0131). */
  function checar(): string | null {
    return depNome.trim() === '' ? 'Informe o nome do depósito.' : null;
  }

  function sair(campo: CampoDoCadastro) {
    setErros((atual) => ({ ...atual, [campo]: checar() }));
  }
  function limpar(campo: CampoDoCadastro) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function gravarDeposito(evento: FormEvent) {
    evento.preventDefault();
    const achados = { nome: checar() };
    setErros(achados);
    if (temErro(achados)) return;
    setRecusa(null);
    criarDeposito.mutate(
      { nome: depNome.trim() },
      {
        /* O retorno é o toast "Depósito cadastrado" (R04, p.32); a janela fecha. */
        onSuccess: aoFechar,
        onError: (falha) => { setRecusa(explicarFalha(falha)); },
      },
    );
  }

  return (
    <form onSubmit={gravarDeposito} noValidate className="flex flex-col gap-6">
      <div className="formulario-grade">
        {/* 0131 · o codigo do deposito e do sistema (decisao 57). */}
        <CodigoDoSistema />
        <div className="campo-largo">
          <Campo id={`${base}-dnome`} rotulo="Nome do depósito" erro={erros['nome'] ?? null}>
            <input
              id={`${base}-dnome`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Almoxarifado central"
              value={depNome}
              onChange={(e) => { setDepNome(e.target.value); limpar('nome'); }}
              onBlur={() => { sair('nome'); }}
              {...marcaDeErro(erros['nome'], `${base}-dnome`)}
            />
          </Campo>
        </div>
      </div>
      <Aviso recusa={recusa} />
      <div className="barra-de-acoes">
        <Botao nivel="terciario" type="button" onClick={aoFechar}>
          Descartar cadastro
        </Botao>
        <Botao nivel="primario" type="submit" disabled={criarDeposito.isPending}>
          Cadastrar depósito
        </Botao>
      </div>
    </form>
  );
}
